import { shallowEqual, useSelector } from 'react-redux';
import { useInfiniteQuery } from 'react-query';

import TradeListService from '../../../services/TradeListService';
import TradeList from '../../models/TradeList';
import { TRADE } from '../../constants/queryDefinition';
import ReducerConstants from '../../constants/reducers';
import { TradePaginationConfig } from '../../types/trade';
import useManufacturerList from '../vehicle/useManufacturerList';

const tradeListService = TradeListService.get();

const useTradeList = (
  tradeListRequest,
  pageNumber,
  previewCriteria,
  activeNetwork,
  listVariant,
  showSaleEvent
) => {
  const loginResponse = useSelector(
    state => state[ReducerConstants.AUTH],
    shallowEqual
  );
  const manufacturers = useManufacturerList();

  // TODO: This is called twice because trade sort method changes for some reason. Investigate this.
  return useInfiniteQuery(
    [
      TRADE.TRADELIST,
      tradeListRequest,
      manufacturers,
      pageNumber,
      previewCriteria,
      activeNetwork,
      listVariant,
      showSaleEvent
    ],
    async ({
      queryKey,
      pageParam = {
        offset: pageNumber * TradePaginationConfig.maxPerPage,
        allPages: []
      }
    }) => {
      const [
        ,
        tradeListParams,
        ,
        ,
        _previewCriteria,
        _activeNetwork
      ] = queryKey;
      let tradeListResponse;

      // check if its a preview list request
      if (Object.keys(_previewCriteria).length > 0) {
        tradeListResponse = await tradeListService.getPreview({
          ...tradeListParams,
          ..._previewCriteria,
          offset: pageParam.offset,
          max: TradePaginationConfig.maxPerPage,
          networkId: _activeNetwork ?? null
        });
      } else {
        tradeListResponse = await tradeListService.getList({
          ...tradeListParams,
          offset: pageParam.offset,
          max: TradePaginationConfig.maxPerPage,
          networkId: _activeNetwork ?? null
        });
      }

      const { tradeSummaries, dealerSummaries, tradeCount } =
        tradeListResponse?.trades || tradeListResponse;

      return new TradeList(
        tradeSummaries,
        dealerSummaries,
        tradeCount,
        0,
        loginResponse?.countryCode,
        manufacturers?.data,
        tradeListResponse?.details,
        listVariant,
        showSaleEvent
      );
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        // Get the total number of trades already fetched to determine offset
        const offset =
          pageNumber * TradePaginationConfig.maxPerPage +
          TradePaginationConfig.maxPerPage;

        // Determines if there are still more trades to be fetched
        // based on the tradecount and how many trades already fetched
        const allPagesIdx =
          allPages && allPages.length > 1 ? allPages.length - 1 : 0;
        const noMoreTrades = allPages[allPagesIdx]?.tradeCount - offset <= 0;

        return noMoreTrades
          ? undefined
          : {
              offset,
              allPages
            };
      },
      getPreviousPageParam: (firstPage, allPages) => {
        // Determine if there are trades to be fetched in the previous page based on page number
        const offset =
          pageNumber * TradePaginationConfig.maxPerPage -
          TradePaginationConfig.maxPerPage;

        // Determines if there are still trades in the previous page to be fetched
        const noMoreTrades = offset < 0;

        return noMoreTrades
          ? undefined
          : {
              offset,
              allPages
            };
      },
      refetchOnMount: true,
      enabled: !!manufacturers,
      staleTime: 0,
      cacheTime: 0,
      refetchOnReconnect: true
    }
  );
};

export default useTradeList;
